import { Instance, SnapshotIn, types } from 'mobx-state-tree'
import { getFormattedDateTimeInUserLocaleAndUTC, parseBackendUtcDateString } from 'utils/datetimeUtils'
import { IsEditingModel } from '../IsEditingModel'
import { ExistingPartNumbers, ExistingPartNumbersModel } from '../PartNumbers/PartNumbersModel'
import { ExistingVehicleModel } from './VehicleModel'

enum PartInstanceLifeCycleStatus {
    UNASSIGNED = "unassigned",
    INSTALLED = "installed",
    REMOVED = "removed",
    UNAIRWORTHY = "unairworthy",
}

export const PartInstanceLifeCycleInfoModel = types.model('PartInstanceLifeCycleInfoModel')
.props({
    status: types.maybe(types.maybeNull(types.enumeration('PartInstanceLifeCycleStatus', Object.values(PartInstanceLifeCycleStatus)))),
    user_name: types.maybe(types.maybeNull(types.string)),
    creation_timestamp: types.maybe(types.maybeNull(types.string)),
    vehicle_name: types.maybe(types.maybeNull(types.string)),
})
.views((self) => ({
    get creationTimestamp(): Date | null | undefined {
        if (self.creation_timestamp === null || self.creation_timestamp === undefined) {
            return self.creation_timestamp
        }
        return parseBackendUtcDateString(self.creation_timestamp)
    },
}))
.views((self) => ({
    get lifeCycleStatusText() {
        switch(self.status) {
            case PartInstanceLifeCycleStatus.UNASSIGNED: 
                return `Part was never assigned to any vehicle`
            case PartInstanceLifeCycleStatus.INSTALLED:
                return `Added to ${self.vehicle_name ?? 'Unknown vehicle'}
                by ${self.user_name ?? 'Unknown user'}
                on ${self.creationTimestamp ? getFormattedDateTimeInUserLocaleAndUTC(self.creationTimestamp) + ' (UTC)' : 'Unknown date'}`;
        }
    }
}))


export enum MaintenanceLimitStatus {
    HEALTHY = "healthy",
    UPCOMING = "upcoming",
    REACHED = "reached",
}

export type ValidationErrors = { serial?: string }

const MaintenanceLimitStatusType = types.enumeration('MaintenanceLimitStatus', Object.values(MaintenanceLimitStatus))

const BasePartInstanceModel = types
  .model("BasePartInstanceModel")
  .props({
    serial: types.maybe(types.string),
    vehicle_id: types.maybeNull(types.reference(ExistingVehicleModel)),
  })
  .views((self) => ({
    get validationErrors(): null | ValidationErrors {
        const errors: ValidationErrors = {}

        if (!self.serial) {
            errors.serial = `*Required field`
        }

        return Object.keys(errors).length ? errors : null
    }
  }))
  .actions((self) => ({
    setSerial(serial: typeof self.serial) {
      self.serial = serial;
    },
  }));


export const ExistingPartInstanceModel = BasePartInstanceModel.named('ExistingPartInstanceModel')
.props({
    id: types.identifierNumber,

    part_number: types.reference(ExistingPartNumbersModel),
    creation_timestamp: types.string,

    maintenance_in_days: types.maybeNull(types.number),
    maintenance_in_days_limit_status: types.maybeNull(MaintenanceLimitStatusType),

    maintenance_in_flights: types.maybeNull(types.number),
    maintenance_in_flights_limit_status: types.maybeNull(MaintenanceLimitStatusType),

    maintenance_in_flight_hours: types.maybeNull(types.number),
    maintenance_in_flight_hours_limit_status: types.maybeNull(MaintenanceLimitStatusType),

    
    days_in_use: types.maybeNull(types.number), 
    days_in_use_limit_status: types.maybeNull(MaintenanceLimitStatusType),

    flights: types.maybeNull(types.number),
    flights_limit_status: types.maybeNull(MaintenanceLimitStatusType),

    flight_hours: types.maybeNull(types.number), 
    flight_hours_limit_status: types.maybeNull(MaintenanceLimitStatusType),
    
    life_cycle_info: types.maybe(types.maybeNull(PartInstanceLifeCycleInfoModel)),
})
.views((self) =>({
    get creationTimestampDate(): Date  {
        return parseBackendUtcDateString(self.creation_timestamp)
    },
    get lifeCycleInfo() {
        return self.life_cycle_info
    }
}))
.actions((self) => ({
    setPartNumber(partNumber: ExistingPartNumbers) {
        self.part_number = partNumber
    },
    setCreationTimestamp(creationDate: Date) {
        self.creation_timestamp = creationDate.toISOString()
    }
}))

export const NewPartInstanceModel = BasePartInstanceModel.named('NewPartInstanceModel')
.props({
    creation_timestamp: types.optional(types.maybeNull(types.string), () => new Date(new Date().setUTCHours(0,0,0,0)).toISOString()),
    part_number: types.maybeNull(types.reference(ExistingPartNumbersModel)),
})
.views((self) => ({
    get creationTimestampDate(): Date | null  {
        if (self.creation_timestamp === null) {
            return null
        }
        return parseBackendUtcDateString(self.creation_timestamp)
    },
}))
.actions((self) => ({
    setPartNumber(partNumber: ExistingPartNumbers) {
        self.part_number = partNumber
    },
    setCreationTimestamp(creationDate: Date) {
        self.creation_timestamp = creationDate.toISOString()
    }
}))

export const NewPartInstanceEditingCloneModel = types.compose('NewPartInstanceEditingCloneModel', IsEditingModel, NewPartInstanceModel)
export const ExistingPartInstanceEditingCloneModel = types.compose('ExistingPartInstanceEditingCloneModel', IsEditingModel, ExistingPartInstanceModel)

export const PartInstanceEditingCloneModel = types.union(ExistingPartInstanceEditingCloneModel, NewPartInstanceEditingCloneModel)

export const ExistingPartInstanceFromBackendModel = types.snapshotProcessor(ExistingPartInstanceModel, {
    preProcessor(snapshot: Omit<SnapshotIn<typeof ExistingPartInstanceModel>, 'part_number'> & { part_number: { id: number } }) {
        return {
            ...snapshot,
            part_number: snapshot.part_number.id
        }
      },
})


export const UserReferenceSchema = types.model('UserReferenceSchema', ({
    name: types.string
}))

export const VehicleReferenceSchema = types.model('VehicleReferenceSchema', {
    id: types.number,
    name: types.string
})

export enum PartInstanceLogActionSchema {
    CREATED = "created",
    INSTALLED = "install",
    REMOVED = "remove",
    MAINTAINED = "maintenance",
    RETIRED = "retire",
    MARKED_UNAIRWORTHY = "marked_unairworthy",
    SERIAL_CHANGED = "serial_changed",
    NUMBER_CHANGED = "number_changed",
}

export const PartInstanceActionLogModel = types.model('PartInstanceActionLogModel')
.props({
    id: types.identifierNumber,
    action_timestamp: types.maybeNull(types.string),
    // part_instance_id: types.reference(ExistingPartInstanceModel),
    user: types.maybeNull(UserReferenceSchema),
    vehicle: types.maybeNull(VehicleReferenceSchema),
    action_type: types.enumeration('PartInstanceLogActionSchema', Object.values(PartInstanceLogActionSchema)),
    description: types.maybe(types.maybeNull(types.string)),
})
.views((self) => ({
    get actionTimestamp(): Date | null {
        if (self.action_timestamp === null) {
            return null
        }
        return parseBackendUtcDateString(self.action_timestamp)
    },
    get actionDisplayText() {
        switch (self.action_type) {
            case PartInstanceLogActionSchema.CREATED:
                return `Created`
            case PartInstanceLogActionSchema.INSTALLED:
                return `Added to vehicle ${self.vehicle?.name ?? '[unknown]'}`
            case PartInstanceLogActionSchema.MAINTAINED:
                return `Inspected`
            case PartInstanceLogActionSchema.MARKED_UNAIRWORTHY:
                return `Marked as Unairworthy`
            case PartInstanceLogActionSchema.NUMBER_CHANGED:
            case PartInstanceLogActionSchema.SERIAL_CHANGED:
                return self.description
            case PartInstanceLogActionSchema.REMOVED:
                return `Removed from vehicle ${self.vehicle?.name ?? '[unknown]'}`
            case PartInstanceLogActionSchema.RETIRED:
                return `Mark as retired`
        }
    }
}))

export type PartInstanceEditingClone = Instance<typeof PartInstanceEditingCloneModel>
export type PartInstanceActionLog = Instance<typeof PartInstanceActionLogModel>
export type ExistingPartInstance = Instance<typeof ExistingPartInstanceModel>